// Pure helpers for the storage explorer ("Ver archivos", integration contract §8).
// No React / Redux / HTTP here.
import { AzureFileOwner } from '../models/Entities/AzureFiles/AzureFileUpload';
import { StorageFileAction } from '../models/Entities/AzureFiles/StorageBrowser';
import { StorageOwnerTypeEnum } from '../models/Enums/AzureFileTypeEnum';

/** Minimal i18next `t` signature, so helpers can be tested with a fake translator. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Display names already known by the app, keyed by getEntityNameKey (e.g. "proveedor_45" → "Laboratorio X"). */
export type StorageOwnerNames = Record<string, string>;

const MAX_PATH_LENGTH = 1024;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/**
 * Normalizes a folder path coming from the URL (?path=): trims and strips leading/trailing "/".
 * Paths the server would reject (contract §8: "..", empty segments, "\", control chars, > 1024)
 * fall back to the root ("").
 */
export const normalizeStoragePath = (raw: string | null | undefined): string => {
  const value = (raw ?? '').trim().replace(/^\/+|\/+$/g, '');
  if (!value) return '';
  if (value.length > MAX_PATH_LENGTH || value.includes('\\') || CONTROL_CHARS.test(value)) return '';
  const segments = value.split('/');
  if (segments.some((segment) => segment.trim() === '' || segment === '..' || segment === '.')) return '';
  return value;
};

/** Parent folder ("" for first-level folders and for the root). */
export const getParentPath = (path: string): string => {
  const normalized = normalizeStoragePath(path);
  const index = normalized.lastIndexOf('/');
  return index <= 0 ? '' : normalized.substring(0, index);
};

/** Last segment of a path (the folder or file name). */
export const getPathName = (path: string): string => {
  const normalized = (path ?? '').replace(/\/+$/g, '');
  const index = normalized.lastIndexOf('/');
  return index < 0 ? normalized : normalized.substring(index + 1);
};

/** Explorer URL for a folder (deep link); the root has no query. */
export const buildFilesExplorerUrl = (path: string): string => {
  const normalized = normalizeStoragePath(path);
  return normalized ? `/files?${new URLSearchParams({ path: normalized }).toString()}` : '/files';
};

// ---- owner folders (contract §2) ----

const OWNER_PREFIX: Record<StorageOwnerTypeEnum, string> = {
  [StorageOwnerTypeEnum.Sponsor]: 'sponsor_',
  [StorageOwnerTypeEnum.Practitioner]: 'practitioner_',
  [StorageOwnerTypeEnum.Provider]: 'proveedor_',
  [StorageOwnerTypeEnum.MedicalInsurance]: 'obrasocial_',
};

/**
 * Folder of the storage owner of an upload target: "proveedor_45", "sponsor_12/protocolo_301",
 * "practitioner_{guid}", "obrasocial_7". Undefined when the owner is incomplete.
 */
export const getOwnerFolderPath = (owner: AzureFileOwner | undefined): string | undefined => {
  if (!owner || !owner.ownerId || !/^[A-Za-z0-9-]+$/.test(owner.ownerId)) return undefined;
  const prefix = OWNER_PREFIX[owner.ownerType];
  if (!prefix) return undefined;
  const ownerFolder = `${prefix}${owner.ownerId}`;
  if (owner.ownerType === StorageOwnerTypeEnum.Sponsor && owner.protocolId !== undefined && owner.protocolId > 0) {
    return `${ownerFolder}/protocolo_${owner.protocolId}`;
  }
  return ownerFolder;
};

// ---- friendly folder names (contract §8) ----

/** File-type folders (Contracts FileTypeEnum names) → i18n key; reuses the upload drawer labels. */
export const FILE_TYPE_FOLDER_LABEL_KEYS: Record<string, string> = {
  ProviderSettlement: 'filesExplorer.fileTypeFolders.providerSettlement',
  SponsorPaymentProposal: 'azureFileUpload.fileTypes.sponsorPaymentProposal',
  ProtocolContract: 'filesExplorer.fileTypeFolders.protocolContract',
  ProviderInvoice: 'azureFileUpload.fileTypes.providerInvoice',
  ProviderServicesPerformed: 'azureFileUpload.fileTypes.providerServicesPerformed',
  SettlementInvoice: 'azureFileUpload.fileTypes.settlementInvoice',
  ServiceReceipt: 'filesExplorer.fileTypeFolders.serviceReceipt',
  PractitionerInvoice: 'filesExplorer.fileTypeFolders.practitionerInvoice',
  PaymentReceipt: 'filesExplorer.fileTypeFolders.paymentReceipt',
};

/** Entity types of "entidad_{Tipo}_{id}" folders → i18n key. */
export const ENTITY_TYPE_LABEL_KEYS: Record<string, string> = {
  provider: 'filesExplorer.entityTypes.provider',
  protocol: 'filesExplorer.entityTypes.protocol',
  settlement: 'filesExplorer.entityTypes.settlement',
  contract: 'filesExplorer.entityTypes.contract',
  sponsor: 'filesExplorer.entityTypes.sponsor',
  practitioner: 'filesExplorer.entityTypes.practitioner',
};

const OWNER_FOLDER_PATTERNS: { pattern: RegExp; labelKey: string }[] = [
  { pattern: /^proveedor_(.+)$/i, labelKey: 'filesExplorer.folderNames.provider' },
  { pattern: /^sponsor_(.+)$/i, labelKey: 'filesExplorer.folderNames.sponsor' },
  { pattern: /^protocolo_(.+)$/i, labelKey: 'filesExplorer.folderNames.protocol' },
  { pattern: /^obrasocial_(.+)$/i, labelKey: 'filesExplorer.folderNames.medicalInsurance' },
];

// ---- entity names ("{id} - {name}") ----

/** Entities whose display name can be resolved (store lists or a lookup by id). */
export type StorageEntityKind = 'provider' | 'sponsor' | 'protocol' | 'settlement' | 'contract' | 'practitioner' | 'service';

export interface StorageEntityRef {
  kind: StorageEntityKind;
  id: string;
}

const ENTITY_NAME_KEY_PREFIX: Record<StorageEntityKind, string> = {
  provider: 'proveedor_',
  sponsor: 'sponsor_',
  protocol: 'protocolo_',
  settlement: 'liquidacion_',
  contract: 'contrato_',
  practitioner: 'practitioner_',
  service: 'servicio_',
};

/** Label (without name) per entity kind: "Proveedor {{id}}", "Protocolo {{id}}", ... */
const ENTITY_LABEL_KEYS: Record<StorageEntityKind, string> = {
  provider: 'filesExplorer.folderNames.provider',
  sponsor: 'filesExplorer.folderNames.sponsor',
  protocol: 'filesExplorer.folderNames.protocol',
  settlement: 'filesExplorer.folderNames.settlement',
  contract: 'filesExplorer.folderNames.contract',
  practitioner: 'filesExplorer.folderNames.practitioner',
  service: 'filesExplorer.folderNames.service',
};

/** Practitioner ids are the person identifier (GUID): the label shows the first 8 characters. */
export const PRACTITIONER_SHORT_ID_LENGTH = 8;
export const shortPractitionerId = (id: string): string =>
  id.length > PRACTITIONER_SHORT_ID_LENGTH ? id.substring(0, PRACTITIONER_SHORT_ID_LENGTH) : id;

/**
 * Ids that can be looked up: positive integers, or the practitioner person identifier (letters, digits, "-").
 * Services (GUID) are only named from the services already in the store, never looked up one by one.
 */
export const isResolvableEntityRef = (ref: StorageEntityRef): boolean => {
  if (ref.kind === 'service') return false;
  return ref.kind === 'practitioner' ? /^[A-Za-z0-9-]+$/.test(ref.id) : Number(ref.id) > 0;
};

/** Key of an entity inside StorageOwnerNames: proveedor_45, sponsor_12, protocolo_31, liquidacion_616. */
export const getEntityNameKey = (kind: StorageEntityKind, id: string | number): string =>
  `${ENTITY_NAME_KEY_PREFIX[kind]}${kind === 'service' ? String(id).toLowerCase() : id}`;

const ENTITY_FOLDER_KINDS: Record<string, StorageEntityKind> = {
  provider: 'provider',
  sponsor: 'sponsor',
  protocol: 'protocol',
  settlement: 'settlement',
  contract: 'contract',
};

/** Name shown for a contract: its description and, when it has none, its number (same rule as the page breadcrumbs). */
export const contractDisplayName = (contract: { contractNumber?: string | null; contractDescription?: string | null } | undefined): string | undefined =>
  contract?.contractDescription?.trim() || contract?.contractNumber?.trim() || undefined;

/**
 * Entity behind a folder name: proveedor_45, sponsor_12, protocolo_31, entidad_Protocol_31,
 * entidad_Settlement_616, entidad_Provider_45. Undefined for anything else (types, practitioners...).
 */
export const parseFolderEntityRef = (name: string | null | undefined): StorageEntityRef | undefined => {
  const practitioner = /^practitioner_([A-Za-z0-9-]+)$/i.exec(name ?? '');
  if (practitioner) return { kind: 'practitioner', id: practitioner[1] };
  const practitionerEntity = /^entidad_practitioner_([A-Za-z0-9-]+)$/i.exec(name ?? '');
  if (practitionerEntity) return { kind: 'practitioner', id: practitionerEntity[1] };
  const owner = /^(proveedor|sponsor|protocolo)_(\d+)$/i.exec(name ?? '');
  if (owner) {
    const prefix = owner[1].toLowerCase();
    const kind: StorageEntityKind = prefix === 'proveedor' ? 'provider' : prefix === 'sponsor' ? 'sponsor' : 'protocol';
    return { kind, id: owner[2] };
  }
  const service = /^entidad_service_([0-9a-f-]{36})$/i.exec(name ?? '');
  if (service) return { kind: 'service', id: service[1].toLowerCase() };
  const entity = /^entidad_([A-Za-z]+)_(\d+)$/i.exec(name ?? '');
  if (entity) {
    const kind = ENTITY_FOLDER_KINDS[entity[1].toLowerCase()];
    if (kind) return { kind, id: entity[2] };
  }
  return undefined;
};

/** Entities referenced by the segments of the given paths (deduplicated, positive numeric ids only). */
export const collectStorageEntityRefs = (paths: (string | null | undefined)[]): StorageEntityRef[] => {
  const refs = new Map<string, StorageEntityRef>();
  paths.forEach((path) =>
    (path ?? '').split('/').forEach((segment) => {
      const ref = parseFolderEntityRef(segment);
      if (ref && isResolvableEntityRef(ref)) refs.set(getEntityNameKey(ref.kind, ref.id), ref);
    })
  );
  return Array.from(refs.values());
};

/** "Protocolo 31 - PRESTO" when the name is known, "Protocolo 31" otherwise (loading, error). */
export const formatStorageEntityLabel = (
  ref: StorageEntityRef,
  t: Translate,
  ownerNames: StorageOwnerNames = {}
): string => {
  const name = ownerNames[getEntityNameKey(ref.kind, ref.id)];
  if (ref.kind === 'service') {
    // "Servicio Traslado al hospital" once the service is in the store; "Servicio 3f2a1b2c" otherwise.
    return name
      ? t('filesExplorer.folderNames.serviceNamed', { name })
      : t(ENTITY_LABEL_KEYS.service, { id: shortPractitionerId(ref.id) });
  }
  if (ref.kind === 'practitioner') {
    // "Profesional Pérez, Juan" once known; "Profesional 3f2a1b2c" while loading or when it fails.
    return name
      ? t('filesExplorer.folderNames.practitionerNamed', { name })
      : t(ENTITY_LABEL_KEYS.practitioner, { id: shortPractitionerId(ref.id) });
  }
  const label = t(ENTITY_LABEL_KEYS[ref.kind], { id: ref.id });
  return name ? t('filesExplorer.folderNames.named', { label, name }) : label;
};

const withKnownName = (label: string, folderName: string, t: Translate, ownerNames: StorageOwnerNames): string => {
  const ref = parseFolderEntityRef(folderName);
  const known = ref ? ownerNames[getEntityNameKey(ref.kind, ref.id)] : undefined;
  return known ? t('filesExplorer.folderNames.named', { label, name: known }) : label;
};

/**
 * Human name of a storage folder:
 *  proveedor_45 → "Proveedor 45" ("Proveedor 45 - Laboratorio X" when the app knows the name),
 *  sponsor_12 → "Sponsor 12", protocolo_31 → "Protocolo 31 - PRESTO", practitioner_x → "Profesional x",
 *  obrasocial_7 → "Obra social 7", entidad_Settlement_616 → "Liquidación 616 - {name}",
 *  ProviderInvoice → "Factura del proveedor". Unknown names are returned as they are.
 */
export const getFriendlyFolderName = (name: string, t: Translate, ownerNames: StorageOwnerNames = {}): string => {
  const fileTypeKey = FILE_TYPE_FOLDER_LABEL_KEYS[name];
  if (fileTypeKey) return t(fileTypeKey);

  const practitioner = /^practitioner_(.+)$/i.exec(name);
  if (practitioner) return formatStorageEntityLabel({ kind: 'practitioner', id: practitioner[1] }, t, ownerNames);

  for (const { pattern, labelKey } of OWNER_FOLDER_PATTERNS) {
    const match = pattern.exec(name);
    if (match) return withKnownName(t(labelKey, { id: match[1] }), name, t, ownerNames);
  }

  const service = /^entidad_service_(.+)$/i.exec(name);
  if (service) return formatStorageEntityLabel({ kind: 'service', id: service[1] }, t, ownerNames);

  // Practitioner invoices folder (contract §12.1): "Profesional Pérez, Juan".
  const practitionerEntity = /^entidad_practitioner_([A-Za-z0-9-]+)$/i.exec(name);
  if (practitionerEntity) return formatStorageEntityLabel({ kind: 'practitioner', id: practitionerEntity[1] }, t, ownerNames);

  // Payment receipts folder (contract §12.1): "Pago #123".
  const payment = /^entidad_payment_(\d+)$/i.exec(name);
  if (payment) return t('filesExplorer.folderNames.payment', { id: payment[1] });

  const entity = /^entidad_([A-Za-z]+)_(.+)$/.exec(name);
  if (entity) {
    const entityKey = ENTITY_TYPE_LABEL_KEYS[entity[1].toLowerCase()];
    const label = t('filesExplorer.folderNames.entity', { entity: entityKey ? t(entityKey) : entity[1], id: entity[2] });
    return withKnownName(label, name, t, ownerNames);
  }

  return name;
};

/** Friendly location of a file: its folders joined with " › " (the file name itself is excluded). */
export const buildFriendlyLocation = (filePath: string, t: Translate, ownerNames: StorageOwnerNames = {}): string =>
  (filePath ?? '')
    .split('/')
    .slice(0, -1)
    .filter(Boolean)
    .map((segment) => getFriendlyFolderName(segment, t, ownerNames))
    .join(' › ');

/** Name lookup cached by id (StorageBrowser state), keyed by getEntityNameKey. */
export interface StorageEntityNameEntry {
  status: 'loading' | 'loaded' | 'failed';
  name?: string;
}

type NamedPerson = { id: number; person?: { businessName?: string } | null };

/**
 * Known entity names: names resolved by id plus the lists already loaded in the store
 * (providers, sponsors, protocols, settlements), keyed by getEntityNameKey.
 */
export const buildStorageOwnerNames = (
  providers: NamedPerson[] | undefined,
  sponsors: NamedPerson[] | undefined,
  protocols?: { id: number; title?: string | null }[],
  settlements?: { id: number; name?: string | null }[],
  resolved?: Record<string, StorageEntityNameEntry>,
  practitioners?: { userId: string; fullName?: string | null }[],
  services?: { id: string; description?: string | null }[],
  contracts?: { id: number; contractNumber?: string | null; contractDescription?: string | null }[]
): StorageOwnerNames => {
  const names: StorageOwnerNames = {};
  Object.entries(resolved ?? {}).forEach(([key, entry]) => {
    const name = entry?.status === 'loaded' ? entry.name?.trim() : undefined;
    if (name) names[key] = name;
  });
  const add = (kind: StorageEntityKind, id: number | string | undefined, name: string | null | undefined) => {
    const value = name?.trim();
    if (value && id !== undefined && id !== null) names[getEntityNameKey(kind, id)] = value;
  };
  (providers ?? []).forEach((provider) => add('provider', provider?.id, provider?.person?.businessName));
  (sponsors ?? []).forEach((sponsor) => add('sponsor', sponsor?.id, sponsor?.person?.businessName));
  (protocols ?? []).forEach((protocol) => add('protocol', protocol?.id, protocol?.title));
  (settlements ?? []).forEach((settlement) => add('settlement', settlement?.id, settlement?.name));
  (practitioners ?? []).forEach((practitioner) => add('practitioner', practitioner?.userId, practitioner?.fullName));
  (services ?? []).forEach((service) => add('service', service?.id?.toLowerCase(), service?.description));
  (contracts ?? []).forEach((contract) => add('contract', contract?.id, contractDisplayName(contract)));
  return names;
};

// ---- files ----

export type StorageFileKind = 'pdf' | 'spreadsheet' | 'document' | 'image' | 'text' | 'other';

const FILE_KINDS: Record<string, StorageFileKind> = {
  pdf: 'pdf',
  xlsx: 'spreadsheet',
  xls: 'spreadsheet',
  csv: 'spreadsheet',
  doc: 'document',
  docx: 'document',
  ppt: 'document',
  pptx: 'document',
  svg: 'image',
  log: 'text',
  json: 'text',
  xml: 'text',
  md: 'text',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  bmp: 'image',
  webp: 'image',
  txt: 'text',
};

const normalizeExtension = (extension: string | null | undefined): string =>
  (extension ?? '').trim().replace(/^\./, '').toLowerCase();

/** Extension of a file, preferring the one returned by the server. */
export const getStorageFileExtension = (file: { name: string; extension?: string | null }): string => {
  const fromServer = normalizeExtension(file.extension);
  if (fromServer) return fromServer;
  const dot = file.name.lastIndexOf('.');
  return dot > 0 ? normalizeExtension(file.name.substring(dot + 1)) : '';
};

export const getStorageFileKind = (extension: string | null | undefined): StorageFileKind =>
  FILE_KINDS[normalizeExtension(extension)] ?? 'other';

/** The browser can show it in a tab (pdf, images, plain text); everything else is downloaded. */
export const canOpenInBrowser = (extension: string | null | undefined): boolean => {
  const kind = getStorageFileKind(extension);
  return kind === 'pdf' || kind === 'image' || kind === 'text';
};

/** What "Abrir" (double click / Enter) does with a file. */
export const getOpenFileAction = (extension: string | null | undefined): StorageFileAction =>
  canOpenInBrowser(extension) ? 'open' : 'download';

/** Case and accent insensitive "contains". */
export const matchesStorageSearch = (values: (string | undefined)[], query: string): boolean => {
  const fold = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  const needle = fold(query.trim());
  if (!needle) return true;
  return values.some((value) => !!value && fold(value).includes(needle));
};
